import { FC, useEffect, useMemo, useState } from "react";
import { calendarService, ICalendarLesson, IDateTimeSlot, TLessonAction, TLessonFaultParty } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { MyInput } from "../../../shared/ui/input";
import { Modal } from "../../../shared/ui/modal";
import { MyTextarea } from "../../../shared/ui/myTextarea";
import {
    createUtcDateFromMoscowInput,
    formatMoscowDateInput,
    formatMoscowTime,
    formatMoscowTimeInput,
} from "../../../shared/lib/helpers/moscowDateTime";
import classes from './calendarLessonActions.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    lesson: ICalendarLesson;
    action: TLessonAction;
    onSuccess: () => void;
}

const SLOT_DURATION_MINUTES = 30
const MS_IN_MINUTE = 60 * 1000

const ACTION_TITLES: Partial<Record<TLessonAction, string>> = {
    LESSON_ACTION_COMPLETE: 'Провести занятие',
    LESSON_ACTION_CANCEL: 'Отменить занятие',
    LESSON_ACTION_RESCHEDULE: 'Перенести занятие',
    LESSON_ACTION_DELETE: 'Удалить занятие',
    LESSON_ACTION_REOPEN_CANCELLED: 'Откатить отмену занятия',
}

const CONFIRM_TITLES: Partial<Record<TLessonAction, string>> = {
    LESSON_ACTION_COMPLETE: 'Точно отметить занятие проведённым?',
    LESSON_ACTION_CANCEL: 'Точно отменить занятие?',
    LESSON_ACTION_RESCHEDULE: 'Точно перенести занятие?',
    LESSON_ACTION_DELETE: 'Точно удалить занятие?',
    LESSON_ACTION_REOPEN_CANCELLED: 'Точно откатить отмену занятия?',
}

const SUCCESS_MESSAGES: Partial<Record<TLessonAction, string>> = {
    LESSON_ACTION_COMPLETE: 'Занятие отмечено проведённым',
    LESSON_ACTION_CANCEL: 'Занятие отменено',
    LESSON_ACTION_RESCHEDULE: 'Занятие перенесено',
    LESSON_ACTION_DELETE: 'Занятие удалено',
    LESSON_ACTION_REOPEN_CANCELLED: 'Отмена занятия откатана',
}

const FAULT_PARTY_OPTIONS: {value: TLessonFaultParty; label: string}[] = [
    {value: 'LESSON_FAULT_PARTY_UNKNOWN', label: 'Неясно'},
    {value: 'LESSON_FAULT_PARTY_STUDENT', label: 'Ученик'},
    {value: 'LESSON_FAULT_PARTY_TUTOR', label: 'Исполнитель'},
    {value: 'LESSON_FAULT_PARTY_STAFF', label: 'Команда'},
]

const getLessonEndDate = (lesson: ICalendarLesson): Date => {
    return new Date(new Date(lesson.startsAt).getTime() + lesson.durationSlots * SLOT_DURATION_MINUTES * MS_IN_MINUTE)
}

const getDateTimeFromInputs = (date: string, time: string): Date | null => {
    return createUtcDateFromMoscowInput(date, time)
}

export const CalendarLessonActions: FC<IProps> = ({open, setOpen, lesson, action, onSuccess}) => {

    const initialEndDate = useMemo(() => getLessonEndDate(lesson), [lesson])
    const isCompleteAction = action === 'LESSON_ACTION_COMPLETE'

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [confirm, setConfirm] = useState<boolean>(false)
    const [comment, setComment] = useState<string>('')
    const [error, setError] = useState<string>('')
    const [startDate, setStartDate] = useState<string>(formatMoscowDateInput(lesson.startsAt))
    const [startTime, setStartTime] = useState<string>(formatMoscowTimeInput(lesson.startsAt))
    const [endDate, setEndDate] = useState<string>(formatMoscowDateInput(initialEndDate))
    const [endTime, setEndTime] = useState<string>(formatMoscowTimeInput(initialEndDate))
    const [faultParty, setFaultParty] = useState<TLessonFaultParty>('LESSON_FAULT_PARTY_UNKNOWN')
    const [isWithin24Hours, setIsWithin24Hours] = useState<boolean>(false)

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    useEffect(() => {
        if(!open) return

        const lessonStartDate = new Date(lesson.startsAt)
        const lessonEndDate = getLessonEndDate(lesson)

        setConfirm(false)
        setComment('')
        setError('')
        setStartDate(formatMoscowDateInput(lessonStartDate))
        setStartTime(formatMoscowTimeInput(lessonStartDate))
        setEndDate(formatMoscowDateInput(lessonEndDate))
        setEndTime(formatMoscowTimeInput(lessonEndDate))
        setFaultParty('LESSON_FAULT_PARTY_UNKNOWN')
        setIsWithin24Hours(false)
    }, [action, lesson, open])

    const onClose = (value: boolean) => {
        if(!value){
            setConfirm(false)
            setError('')
        }
        setOpen(value)
    }

    const getNewSlot = (): IDateTimeSlot | null => {
        const startsAt = getDateTimeFromInputs(startDate, startTime)
        const endsAt = getDateTimeFromInputs(endDate, endTime)

        if(!startsAt || !endsAt){
            setError('Заполните дату и время переноса')
            return null
        }

        const diffMinutes = (endsAt.getTime() - startsAt.getTime()) / MS_IN_MINUTE

        if(diffMinutes <= 0){
            setError('Окончание занятия должно быть позже начала')
            return null
        }

        if(diffMinutes % SLOT_DURATION_MINUTES !== 0){
            setError('Длительность должна быть кратна 30 минутам')
            return null
        }

        return {
            startsAt: startsAt.toISOString(),
            durationSlots: diffMinutes / SLOT_DURATION_MINUTES,
        }
    }

    const getActualEndsAt = (): Date | null => {
        const startsAt = new Date(lesson.startsAt)
        const endsAt = getDateTimeFromInputs(endDate, endTime)

        if(!endsAt){
            setError('Заполните дату и время окончания')
            return null
        }

        const diffMinutes = (endsAt.getTime() - startsAt.getTime()) / MS_IN_MINUTE

        if(diffMinutes <= 0){
            setError('Окончание занятия должно быть позже начала')
            return null
        }

        if(diffMinutes % SLOT_DURATION_MINUTES !== 0){
            setError('Окончание должно попадать в 30-минутную сетку')
            return null
        }

        return endsAt
    }

    const onPrepareConfirm = () => {
        const trimmedComment = comment.trim()

        if(isCompleteAction){
            if(!getActualEndsAt()) return
        }

        if(action === 'LESSON_ACTION_CANCEL' && !trimmedComment){
            setError('Укажите причину отмены')
            return
        }

        if(action === 'LESSON_ACTION_RESCHEDULE'){
            if(!trimmedComment){
                setError('Укажите причину переноса')
                return
            }

            if(!getNewSlot()) return
        }

        setError('')
        setConfirm(true)
    }

    const onSubmit = async () => {
        try{
            setIsLoading(true)

            if(isCompleteAction){
                const actualEndsAt = getActualEndsAt()
                if(!actualEndsAt) return

                await calendarService.completeLesson({
                    lessonId: lesson.id,
                    actualEndsAt: actualEndsAt.toISOString(),
                })
            }

            if(action === 'LESSON_ACTION_CANCEL'){
                await calendarService.cancelLesson({
                    lessonId: lesson.id,
                    comment: comment.trim(),
                    faultParty,
                    isWithin24Hours,
                })
            }

            if(action === 'LESSON_ACTION_DELETE'){
                await calendarService.deleteLesson({lessonId: lesson.id})
            }

            if(action === 'LESSON_ACTION_REOPEN_CANCELLED'){
                await calendarService.reopenCancelledLesson({lessonId: lesson.id})
            }

            if(action === 'LESSON_ACTION_RESCHEDULE'){
                const newSlot = getNewSlot()
                if(!newSlot) return

                await calendarService.rescheduleLesson({
                    lessonId: lesson.id,
                    newSlot,
                    comment: comment.trim(),
                    faultParty,
                    isWithin24Hours,
                })
            }

            setGlobalMessage({message: SUCCESS_MESSAGES[action] ?? 'Действие выполнено', type: 'ok'})
            onClose(false)
            onSuccess()
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при выполнении действия с занятием', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const onConfirmOpenChange = (value: boolean) => {
        setConfirm(value)
    }

    const isConfirmVisible = confirm

    return (
        <Modal open={open} setOpen={onClose}>
            {isConfirmVisible ? (
                <ConfirmationAction
                    title={CONFIRM_TITLES[action] ?? 'Точно выполнить действие?'}
                    setOpen={onConfirmOpenChange}
                    onClick={onSubmit}
                    type={action === 'LESSON_ACTION_CANCEL' || action === 'LESSON_ACTION_DELETE' ? 'delete' : 'send'}
                    isLoading={isLoading}
                />
            ) : (
                <section className={classes.container}>
                    <section className={classes.header}>
                        <h3>{ACTION_TITLES[action] ?? 'Действие с занятием'}</h3>
                        <button type="button" onClick={() => onClose(false)} className={classes.closeButton}>×</button>
                    </section>

                    {action === 'LESSON_ACTION_CANCEL' && (
                        <>
                            <section className={classes.faultParty}>
                                <span>По чьей причине</span>
                                <section>
                                    {FAULT_PARTY_OPTIONS.map(option => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={faultParty === option.value ? classes.selected : ''}
                                            onClick={() => setFaultParty(option.value)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </section>
                            </section>
                            <label className={classes.lateToggle}>
                                <input
                                    type="checkbox"
                                    checked={isWithin24Hours}
                                    onChange={event => setIsWithin24Hours(event.target.checked)}
                                />
                                <span>
                                    <strong>Договорились менее чем за 24 часа</strong>
                                    <small>Учитывается для лимита и санкций с 4-го случая.</small>
                                </span>
                            </label>
                            <MyTextarea
                                title="Причина отмены"
                                value={comment}
                                setValue={setComment}
                                setError={setError}
                                placeholder="Например: ученик предупредил, что не сможет прийти"
                            />
                        </>
                    )}

                    {isCompleteAction && (
                        <>
                            <section className={classes.completeSummary}>
                                <section>
                                    <span>Запланировано</span>
                                    <strong>{`${formatMoscowTime(lesson.startsAt)}–${formatMoscowTime(initialEndDate)}`}</strong>
                                </section>
                                <section>
                                    <span>Начало</span>
                                    <strong>{formatMoscowTime(lesson.startsAt)}</strong>
                                </section>
                            </section>
                            <section className={classes.formGrid}>
                                <MyInput type="date" title="Дата окончания" value={endDate} setValue={setEndDate} setError={setError} />
                                <MyInput type="time" title="Фактическое окончание" step={SLOT_DURATION_MINUTES * 60} value={endTime} setValue={setEndTime} setError={setError} />
                            </section>
                            <p className={classes.hint}>Начало занятия не меняется. Начисление исполнителю и списание ученика будут рассчитаны по фактической длительности.</p>
                            {error && <p className={classes.error}>{error}</p>}
                        </>
                    )}

                    {action === 'LESSON_ACTION_RESCHEDULE' && (
                        <>
                            <section className={classes.formGrid}>
                                <MyInput type="date" title="Дата начала" value={startDate} setValue={setStartDate} setError={setError} />
                                <MyInput type="time" title="Время начала" step={SLOT_DURATION_MINUTES * 60} value={startTime} setValue={setStartTime} setError={setError} />
                                <MyInput type="date" title="Дата окончания" value={endDate} setValue={setEndDate} setError={setError} />
                                <MyInput type="time" title="Время окончания" step={SLOT_DURATION_MINUTES * 60} value={endTime} setValue={setEndTime} setError={setError} />
                            </section>
                            <section className={classes.faultParty}>
                                <span>По чьей причине</span>
                                <section>
                                    {FAULT_PARTY_OPTIONS.map(option => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={faultParty === option.value ? classes.selected : ''}
                                            onClick={() => setFaultParty(option.value)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </section>
                            </section>
                            <label className={classes.lateToggle}>
                                <input
                                    type="checkbox"
                                    checked={isWithin24Hours}
                                    onChange={event => setIsWithin24Hours(event.target.checked)}
                                />
                                <span>
                                    <strong>Договорились менее чем за 24 часа</strong>
                                    <small>Учитывается для лимита и санкций с 4-го случая.</small>
                                </span>
                            </label>
                            <MyTextarea
                                title="Причина переноса"
                                value={comment}
                                setValue={setComment}
                                setError={setError}
                                error={error}
                                placeholder="Например: перенос по просьбе ученика"
                            />
                        </>
                    )}

                    <section className={classes.footer}>
                        <section className={classes.secondaryButton}>
                            <MyButtonSecondary onClick={() => onClose(false)}>
                                Отмена
                            </MyButtonSecondary>
                        </section>
                        <section className={classes.primaryButton}>
                            <MyButton onClick={onPrepareConfirm}>
                                {action === 'LESSON_ACTION_RESCHEDULE' ? 'Перенести' : action === 'LESSON_ACTION_DELETE' ? 'Удалить' : action === 'LESSON_ACTION_REOPEN_CANCELLED' ? 'Откатить' : isCompleteAction ? 'Провести' : 'Продолжить'}
                            </MyButton>
                        </section>
                    </section>
                </section>
            )}
        </Modal>
    )
}
